import { Controller } from "@hotwired/stimulus"

/**
 * Live event-instance hero ticker.
 *
 * Re-renders the "elapsed in / remaining" lines from authoritative ISO
 * timestamps in data attributes so a live event's hero clock keeps up
 * without a server round trip. The Rails-rendered initial values stay
 * visible until the first tick (default 30s), so first paint is always
 * localized.
 *
 * Usage in ERB (see `app/views/event_instances/_state_in_progress.html.erb`):
 *
 *   <div data-controller="live-time"
 *        data-live-time-starts-at-value="2026-05-21T12:00:00Z"
 *        data-live-time-ends-at-value="2026-05-21T13:00:00Z"
 *        data-live-time-elapsed-template-value="%{value} in"
 *        data-live-time-remaining-template-value="%{value} remaining"
 *        data-live-time-just-started-value="Just started"
 *        data-live-time-ended-value="Ended">
 *     <p data-live-time-target="elapsed">…server-rendered initial…</p>
 *     <p data-live-time-target="remaining">…server-rendered initial…</p>
 *   </div>
 *
 * The templates ship as fully-localized strings with the literal sentinel
 * `%{value}` where the computed duration goes (e.g. "38m" or "1h 5m"). The
 * controller never invents copy — it only swaps the value in.
 */
export default class extends Controller<HTMLElement> {
  static values = {
    startsAt: String,
    endsAt: String,
    elapsedTemplate: String,
    remainingTemplate: String,
    justStarted: String,
    ended: String,
    tickMs: { type: Number, default: 30_000 },
    // When set, the element hides itself until `endsAt` is this close, so a
    // deadline stays out of the way until it is worth interrupting for.
    withinMs: { type: Number, default: 0 },
  }

  static targets = ["elapsed", "remaining"]

  declare startsAtValue: string
  declare endsAtValue: string
  declare elapsedTemplateValue: string
  declare remainingTemplateValue: string
  declare justStartedValue: string
  declare endedValue: string
  declare tickMsValue: number
  declare withinMsValue: number

  declare readonly hasElapsedTarget: boolean
  declare readonly elapsedTarget: HTMLElement
  declare readonly hasRemainingTarget: boolean
  declare readonly remainingTarget: HTMLElement

  private timer: ReturnType<typeof setInterval> | null = null

  connect() {
    this.update()
    const tick = Math.max(1_000, this.tickMsValue || 30_000)
    this.timer = setInterval(() => this.update(), tick)
  }

  disconnect() {
    if (this.timer !== null) {
      clearInterval(this.timer)
      this.timer = null
    }
  }

  update() {
    const ends = this.parseDate(this.endsAtValue)
    if (!ends) return

    const starts = this.parseDate(this.startsAtValue)
    const now = Date.now()

    if (this.withinMsValue > 0) {
      this.element.classList.toggle("hidden", ends.getTime() - now > this.withinMsValue)
    }

    if (starts && this.hasElapsedTarget) {
      const cap = Math.min(now, ends.getTime())
      const elapsedMs = Math.max(0, cap - starts.getTime())
      const elapsedText = elapsedMs <= 30_000 && now < ends.getTime()
        ? this.justStartedValue || this.formatRelative(elapsedMs)
        : this.applyTemplate(this.elapsedTemplateValue, this.formatRelative(elapsedMs))
      this.elapsedTarget.textContent = elapsedText
    }

    if (this.hasRemainingTarget) {
      const remainingMs = Math.max(0, ends.getTime() - now)
      const remainingText = remainingMs <= 0
        ? this.endedValue || this.applyTemplate(this.remainingTemplateValue, this.formatRelative(0))
        : this.applyTemplate(this.remainingTemplateValue, this.formatRelative(remainingMs))
      this.remainingTarget.textContent = remainingText
    }
  }

  private parseDate(value: string): Date | null {
    if (!value) return null
    const parsed = new Date(value)
    return Number.isNaN(parsed.getTime()) ? null : parsed
  }

  // Compact "Xh Ym" / "Ym" / "0m" formatting. Mirrors the Rails-side
  // `format_event_duration_minutes` helper.
  private formatRelative(ms: number): string {
    if (ms <= 0) return "0m"
    const totalMinutes = Math.round(ms / 60_000)
    if (totalMinutes === 0) return "0m"
    if (totalMinutes < 60) return `${totalMinutes}m`
    const hours = Math.floor(totalMinutes / 60)
    const minutes = totalMinutes % 60
    return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`
  }

  private applyTemplate(template: string, value: string): string {
    if (!template) return value
    return template.replace("%{value}", value)
  }
}
